module.exports = {
    Description: {
        StaticInfo: `<p>
        In any project, it is essential to start the process with a review of all data and information available. 
        Data quality-related problems can cost E&P companies millions of dollars annually. Revenue opportunities are lost as a result of the inability to make strategic business decisions in a timely manner. To solve data quality problems it is necessary to address the root causes of the data quality issues
        </p>`,
        SectionsInfo: []
    },
    Overview: {
        StaticInfo: `
   <p>
       To start any project, it is essential to start the process with a review of the basic data of the area with
       regard to: properties of the reservoir (s), rock properties, types of existing or required wells,
       type-productivity and historical review of the area of interest.<br>
       <br>
       The objective is to make sure that all the parameters to be used in the subsequent analysis of the procedure or
       study to be carried out are <strong>validated</strong>, in order to eliminate as much as possible the
       uncertainty in the activity. In this process, the information and data related to geology, fluids, pressures,
       rock-fluid interaction, well data, general information of the reservoir and everything required to carry out the
       subsequent analysis are clearly separated and adequately checked for validity.<br>
       <br>
       Our purpose is to help you with a diagnosis of the available database that concerns the operation or study to be
       undertaken in terms of quantity, quality and relevance, in order to guarantee that this process covers all the
       minimum aspects necessary to start the process and carry out the operation<br>
       <br>
       Accurate data implies high data quality. This is one of many data management challenges faced by the Oil & Gas
       industry. For any data set to be qualified as being of good quality, it must possess some essential
       characteristics, among them:<br>
       <ul>
           <li><strong>Accuracy:</strong> Data accuracy measures the degree to which the data correctly reflects the realities
               of the conditions modelled</li>
           <li><strong>Completeness:</strong> One approach to completeness is to ensure that certain attributes <strong>always</strong> have
               assigned values in a data set</li>
           <li><strong>Consistency:</strong> Quality data must be consistent across the board, such that data set values or
               naming conventions must be uniform wherever such data sets occurs</li>
           <li><strong>Timeliness:</strong> This refers to the degree to which information is current with the environment
               that it models</li>
       </ul>
   </p>
   `,
        SectionsInfo: []
    },
    Inputs: {
        StaticInfo: `<p>
   Consider uploading or providing the consultant access to the database. The consultant will help you (with prior
   authorization) to analyze the data you provide, in order to assure that it covers all the aspects needed for a
   successful operation. This review is iterative and seeks to perform the data review and validation process.<br>
   <br>
   General data required for reservoir studies will include:<br>`,
        SectionsInfo: [{
                title: "a) General Data",
                content: `
            <ul>
           <li>Country</li>
           <li>Company</li>
           <li>Basin</li>
           <li>Block</li>
           <li>Field</li>
           <li>Location</li>
           <li>Reference system</li>
           <li>Coordinates</li>
       </ul>
            `
            },
            {
                title: "b) Basic Reservoir Geology",
                content: `
            <ul>
<li>Basin / Location</li>
<li>Reservoir Formation Name </li>
<li>Formation type (Geology: Siliciclastic, Carbonate, Shale) Description</li>
<li>Main objective - Flow unit name (Sand or shale)</li>
<li>Porosity</li>
<li>Total Permeability</li>
<li>Grain size</li>
<li>Pore size</li>
<li>Fluid loss tests</li>
<li>Formation Gross Thickness</li>
<li>Net pay thickness</li>
<li>Trap type (structural, stratigraphic, combined, shale)</li>
<li>Maps: Structural, porosity and thickness</li>
<li>Stratigraphic column: Regional and local</li>
<li>Geological model and/or geological studies</li>
<li>Well logs available </li>

       </ul>
            `
            },
            {
                title: "c) Basic Reservoir Information",
                content: `
            <ul>
<li>Reservoir average depth</li>
<li>Reservoir average temperature</li>
<li>Reservoir pressure, initial</li>
<li>Reservoir pressure, current</li>
<li>Reservoir pressure history</li>
<li>Production mechanism</li>
<li>Average porosity</li>
<li>Average oil saturation (initial)</li>
<li>Average gas Saturation (initial)</li>
<li>Average water saturation (initial)</li>
<li>Initial hydrocarbon volumes in place</li>
<li>Reserves and resources reports</li>
<li>Lifting method</li>
<li>Oil API gravity</li>
<li>Oil viscosity</li>
<li>Oil formation volume factor</li>
<li>Oil asphaltene content</li>
<li>Oil emulsifying tendencies</li>
<li>Oil paraffin forming characteristics</li>
<li>Gas specific gravity</li>
<li>Gas viscosity</li>
<li>Gas formation volume factor</li>
<li>Gas liquid content</li>
<li>Gas N2, CO2, and H2S mole fraction</li>
<li>Water specific gravity</li>
<li>Water salinity</li>
<li>Water volumetric factor</li>

        </ul>
            `
            },
            {
                title: "d) Basic Well information",
                content: `
            <ul>
<li>Existing wells (Injector, producer, status and classification, current condition, vertical, horizontal, etc.)</li>
<li>Well Name</li>
<li>Well coordinates (Latitude – Longitude)</li>
<li>Well coordinates UTM</li>
<li>Measured depth to mid-perfs TVD</li>
<li>Measured depth to mid-perfs MD</li>
<li>Bottom hole temperature</li>
<li>Well logs available (open Hole / cased hole)</li>
<li>Production logs</li>
<li>Cement conditions related logs - CBL-VDL</li>
<li>Drilling program and history for each existing well</li>
<li>Flowing surface pressure</li>
<li>Flowing surface temperature</li>
<li>Mechanical diagram of wells</li>
<li>Productivity index per  well</li>
<li>Programs and history of completion and well rehabilitation-repair events</li>
<li>Tubing inside diameter</li>
<li>Tubing length MD and true vertical depth TVD</li>
<li>Tubing outside diameter</li>
<li>Well production status </li>
<li>Wellbore radius</li>
<li>Wellhead configuration</li>
<li>Historical production and Injection reports per well and reservoir (oil, water and gas)</li>


       </ul>
            `
            },
            {
                title: "e) Advanced Reservoir Information",
                content: `
            <ul>
<li>Fracture geometry parameters</li>
<li>Elastics Modules: Young – Poisson ratio</li>
<li>Decline curves</li>
<li>PVT analysis, including  API Gravity and its areal and vertical variation</li>
<li>Fluid characterization tests or analysis (water, gas and oil)</li>
<li>Pressure buildup tests</li>
<li>Petrophysical evaluation and model (sand thickness, permeability, porosity, water saturation, etc.)</li>
<li>Core analysis (wettability, permeability, etc.)</li>
<li>Original Oil, Gas in place (OOIP, OGIP)</li>
<li>Seismic data and previous interpretation</li>
<li>Reservoir studies / static model (geocell) / simulation models</li>
<li>Details of the existing  surface infrastructure  for the handling of fluids</li>
<li>Production forecast parameters</li>
<li>Production history of neighbouring wells</li>
<li>Previous fracture jobs in the area</li>
<li>Previous fracture mechanics data such as:</li>
<ul>
<li>Fracturing fluid coefficient</li>
<li>Fracture geometry model</li>
<li>Fracture LOT (Leak-off test) data</li>
<li>Minimum horizontal stress</li>
<li>Fracture gross height and area</li>

</ul>
       </ul>
            `
            },
            {
                title: "f) Operational Constraints",
                content: `
            <ul>
           <li>Maximum Pressure allowed before shut-down</li>
           <li>Maximum allowable pump rate</li>
           <li>Maximum proppant concentration</li>
           <li>Maximum surface injection pressure</li>
       </ul>
            `
            },
            {
                title: "g) Economics and other relevant data",
                content: `
            <ul>
            <li>Field operation cost</li>
            <li>Maintenance Costs</li>
            <li>Field logistics and transportation costs</li>
            <li>Stimulation or other operations processes cost</li>
        </ul>
            `
            }
        ]
    },
    Deliverables: {
        StaticInfo: `
        <p>
        All the information collected, hard data or correlation data, or from neighboring or similar areas, requires a
        review, verification of coherence and consistency, as well as a diagnosis of critical parameters that are acceptable or
        violated by the evidence.<br>
        <br>
        In this section (with prior authorization) the consultant will help you generate an accurate Diagnostic Report of
        the available database in terms of: quantity, quality and relevance, of the Process to be used at a later stage of
        study, design or development.
        </p>
        `,
        SectionsInfo: []
    }
}